/**
 * Performance profile resolved once at app boot.
 *
 * The tier is the user-facing axis ("how much can this device chew?"),
 * everything below it is the budget the rest of the app must respect.
 * Modules don't sniff the platform themselves: they receive (or resolve)
 * a `PerfProfile` and read flat fields off it. That keeps the
 * platform-specific knowledge in `lib/perf/` and lets us add a user toggle
 * or an FPS watchdog later without touching 15 modules.
 *
 * **FPS and Pixi resolution are intentionally not capped per tier.** Users
 * notice 30 Hz scrolling instantly (it reads as a glitch on a music app),
 * and a sub-`devicePixelRatio` Pixi resolution makes 1-px measure lines
 * shimmer when scrolled. We dial down audio + visual effects instead, both
 * of which the user feels far less. (Le seul garde-fou est global et non par
 * palier : le ratio Pixi est plafonné à 2, au-delà le backing store grossit
 * sans gain visible — voir `Engine.start`.)
 */
export type PerfTier = "low" | "balanced" | "high";

export interface RenderProfile {
  /** Pixi MSAA — disabled on `low` tier to skip a full-screen render pass. */
  antialias: boolean;
}

export interface AudioProfile {
  /** Tone.Reverb is an FFT convolution per audio frame — heavy on old iPads. */
  enableReverb: boolean;
  /**
   * Salamander Grand Piano sampling strategy.
   * - `single`: load one velocity layer (v8) and reuse it for all dynamics.
   * - `lazy`: start with v8, transparently fetch additional layers as the
   *   user plays varied dynamics (each extra layer ≈ 30 decoded MP3 buffers,
   *   ~150 MB of RAM).
   */
  samplerStrategy: "single" | "lazy";
  /**
   * Hard cap on cumulative decoded sample memory across all synths.
   * Safari iOS jetsam-kills the tab around ~400 MB of WebAudio buffers,
   * desktop browsers tolerate well over 1 GB. The cap stops `lazy` from
   * loading layers that would push us over.
   */
  sampleBudgetMB: number;
  /**
   * Tampon de sortie demandé à l'`AudioContext` (`latencyHint`).
   *
   * C'est le dernier maillon avant le haut-parleur, et le seul qu'on ne
   * traverse pas : le navigateur remplit ce tampon à l'avance, donc tout ce
   * qu'il contient est du retard déjà acquis. Sur un poste fixe il pèse
   * couramment 15 à 25 ms — depuis que la fenêtre d'anticipation de Tone est
   * fixée à zéro (cf. `lib/audio/tone-context.ts`), c'est le poste le plus
   * lourd du budget de latence local.
   *
   * `0` demande le plus court que le pilote accepte.
   *
   * # Ce qu'il change vraiment, et ce qu'il ne peut pas changer
   *
   * `AudioContext.baseLatency` est la part que ce réglage pilote — le tampon
   * entre le graphe et le système. `outputLatency` est la suite du chemin :
   * pilote, mixeur du système, matériel. Aucune page web ne la réduit. Ce qui
   * s'affiche dans le panneau live est la **somme** des deux, donc une valeur
   * qui ne descendra pas jusqu'à zéro quoi qu'on demande. `__virtuosa.state()`
   * donne la décomposition, et c'est elle qu'il faut lire avant d'espérer.
   *
   * # Ce que la mesure a montré, et pourquoi il reste réservé à `high`
   *
   * Le 2026-09-10, sur un iPad en palier `balanced` — donc avec
   * `"interactive"` — le relevé donnait :
   *
   * ```
   * baseLatency   0,00267 s   = 128 échantillons à 48 kHz, un quantum de rendu
   * outputLatency 0,016 s     = pilote + session audio du système + matériel
   * ```
   *
   * Autrement dit le tampon que ce réglage pilote était **déjà au plancher**,
   * sans qu'on ait rien demandé : `"interactive"` obtient le quantum, et
   * `0` ne peut pas faire mieux. Les 16 ms restantes — 85 % du total — sont
   * hors de portée d'une page web : sous iOS, la durée du tampon d'entrée-sortie
   * est celle que Safari a négociée avec la session audio, et aucune API web ne
   * la change.
   *
   * On a donc essayé d'étendre `0` à `balanced` pour l'iPad, puis on est revenu
   * en arrière : c'était accepter le risque de craquements — le pire des
   * défauts audio, audible sur *chaque* note — contre un gain nul et mesuré
   * comme tel. `0` reste sur `high`, où il n'a jamais rien coûté.
   *
   * Le corollaire vaut d'être retenu : quand « sa sortie audio » pèse une
   * quinzaine de millisecondes, il n'y a **rien à gagner côté studio**. Les
   * leviers qui restent sont l'appareil d'écoute (le Bluetooth ajoute cent
   * millisecondes et plus), le réseau, et le mode « jouer ensemble » qu'on peut
   * couper.
   *
   * ⚠️ Il ne se règle **qu'à la création** de l'`AudioContext` : ni un
   * changement de palier en cours de séance, ni l'auto-dégradation du
   * `PerfCoordinator` ne peuvent le reprendre. Il prend effet au rechargement.
   */
  outputLatencyHint: AudioContextOptions["latencyHint"];
}

export interface EffectsProfile {
  /** Whether note-press visual effects (halos, particles) run at full density. */
  particlesEnabled: boolean;
}

export interface PerfProfile {
  tier: PerfTier;
  render: RenderProfile;
  audio: AudioProfile;
  effects: EffectsProfile;
  /** `auto` = derived from device capabilities; `user-forced` = manual override (skip the FPS watchdog if/when we add one). */
  source: "auto" | "user-forced";
}

const PRESETS: Record<PerfTier, Omit<PerfProfile, "tier" | "source">> = {
  low: {
    render: { antialias: false },
    audio: { enableReverb: false, samplerStrategy: "single", sampleBudgetMB: 200, outputLatencyHint: "interactive" },
    effects: { particlesEnabled: false },
  },
  balanced: {
    render: { antialias: true },
    audio: { enableReverb: false, samplerStrategy: "single", sampleBudgetMB: 400, outputLatencyHint: "interactive" },
    effects: { particlesEnabled: true },
  },
  high: {
    render: { antialias: true },
    audio: { enableReverb: true, samplerStrategy: "lazy", sampleBudgetMB: 800, outputLatencyHint: 0 },
    effects: { particlesEnabled: true },
  },
};

export function profileForTier(tier: PerfTier, source: PerfProfile["source"] = "auto"): PerfProfile {
  return { tier, source, ...PRESETS[tier] };
}
